import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from "react";
import { ContentWrapper, ContinueButton, Form, InputBox, ItemWrapper } from "../styles";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CustomSelect from "../../../../components/common/CustomSelect";
import { Input } from "../../../../components/common/Input";
import { accountTypes, emailRegex } from "../../utils";
import { Button } from "../../../../components/common/Button";
import { savedPayoutV2Data } from "../../redux/formDataSlice";
import { useBankList } from "../../../../app/hooks/useBankList";
import { InputPhone } from "../../../../components/common/InputPhone";
import Loader from "../../../../components/common/Loader";
import { resolveZarAccount } from "../../api";
import * as Sentry from "@sentry/react";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import { accountNameValidator, validateAccountName } from "../../../../utils/validateAccountName";

type SelectedBankObject = {
    label: string;
    value: string;
    originalCode?: string;
};

type ZARBeneficiaryTypes = {
    handleNext?: () => void;
    handlePrevious?: () => void;
    paymentMethod?: string;
    paymentMethodName?: string;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    paymentDescription?: string;
    bankCode?: string;
    selectedBank?: SelectedBankObject;
    accountType?: string;
    description?: string;
    IdentityDoc?: string;
    identityNumber?: string;
    recipientAddress?: string;
    recipientEmail?: string;
    phoneNumber?: string;
    identityType?: string;
};

const Beneficiary = ({ handleNext, handlePrevious }: ZARBeneficiaryTypes) => {
    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
        setValue,
        watch
    } = useForm<ZARBeneficiaryTypes>({mode: "onChange"});

    const dispatch = useAppDispatch();

    const { businessId } = useAppSelector((state) => state.users);
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const { onShowAlert } = useContext(NetworkErrorAlertContext);
    const [userData, setUserData] = useState<ZARBeneficiaryTypes>();
    const [loading, setLoading] = useState(false)

    const country = "ZAR"
    const { selectBanks } = useBankList({ country, businessId })

    const accountTypeWatch = watch("accountType")
    const identityTypeWatch = watch("IdentityDoc")

    const identityOptions = [
        {
            value: "IdentityNumber",
            label: "Identity number",
        },
        {
            value: "passportNumber",
            label: "Passport number",
        },
    ];

    const identityLabel =
        identityTypeWatch === "IdentityNumber"
            ? "Enter the identity number"
            : "Enter the passport number";

    useEffect(() => {
        if (getSavedData) {
            Object.keys(getSavedData).forEach((key) => {
                setValue(
                    key as keyof ZARBeneficiaryTypes,
                    getSavedData[key as keyof ZARBeneficiaryTypes],
                );
                setValue('bankName', getSavedData.bankCode)
            });
        }
    }, [getSavedData, setValue]);

    const handleDataUpdate = (field: keyof ZARBeneficiaryTypes, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleBankChange = (selectedModifiedValue) => {
        const positionOfUnderscore = selectedModifiedValue?.indexOf("_");
        const originalBankCode = selectedModifiedValue?.substring(0, positionOfUnderscore);

        const selectedBank = selectBanks.find((b) => b.value === selectedModifiedValue)

        setValue("bankCode", originalBankCode);
        handleDataUpdate("bankName", selectedBank?.label);
        handleDataUpdate("selectedBank", selectedBank);
        handleDataUpdate("bankCode", originalBankCode)
    };

    const updateIdentiTy = (data) => {
        if (data === "IdentityNumber") {
            return "identityNumber";
        }
        return data;
    };

    const resolveZARAccountNumber = async (data) => {
        setLoading(true)
        const ZarPayload = {
            accountNumber: data.accountNumber,
            accountName: data.accountName,
            bankCode: data.bankName,
            accountType: data.accountType,
            documentNumber: data.identityNumber || data.identityType,
            countryCode: "ZA",
            documentType: updateIdentiTy(data.IdentityDoc) || "businessRegistrationNumber",
            businessId: businessId,
        }

        await resolveZarAccount(ZarPayload)
            .then((res) => {
                setLoading(false);
                onSubmit(data)
            })
            .catch((errr) => {
                onShowAlert("", errr?.response?.data?.error);
                setLoading(false);
                Sentry.captureException(errr);
            });
    }

    const onSubmit: SubmitHandler<ZARBeneficiaryTypes> = useCallback(
        (data) => {
            const selectedBank = selectBanks?.find((b) => b.value === data.bankName)

            const combinedData = {
                ...userData,
                ...data,
                bankName: selectedBank?.label,
                bankCode: data.bankName
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, userData, selectBanks],
    );

    return (
        <ContentWrapper>
            <Loader isLoading={loading} />
            <Form>
                <InputBox>
                    <Controller
                        name="bankName"
                        control={control}
                        rules={{
                            required: "Please input the beneficiary's Bank name",
                        }}
                        render={({ field }) => (
                            <CustomSelect
                                placeholder={"Select bank"}
                                $width="100%"
                                field={field}
                                disableLabel
                                options={selectBanks}
                                value={field.value}
                                onChange={(selectedValue) => {
                                    field.onChange(selectedValue)
                                    handleBankChange(selectedValue)
                                }}
                                showSearch
                                filterOption={(inputValue, option) =>
                                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                lightBorder={true}
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.bankName?.message}
                    </span>
                </InputBox>

                <InputBox>
                    <Controller
                        name="accountNumber"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account number"
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Input
                                type="number"
                                label={"Account number"}
                                labelColor="#FFFFFF"
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    onChange(e.target.value);
                                }}
                                value={value}
                                {...field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountNumber?.message}

                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{
                            validate: accountNameValidator(validateAccountName)
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Account name"
                                labelColor="#FFFFFF"
                                field={field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountName?.message}
                    </span>

                </InputBox>


                <InputBox>
                    <Controller
                        name="accountType"
                        control={control}
                        rules={{
                            required: "The account type is required",
                        }}
                        render={({ field }) => (
                            <CustomSelect
                                placeholder="Account Type"
                                $width="100%"
                                field={field}
                                disableLabel
                                options={accountTypes}
                                onChange={field.onChange}
                                lightBorder={true}
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountType?.message}

                    </span>
                </InputBox>

                {accountTypeWatch === "personal" && (
                    <InputBox>
                        <Controller
                            name="IdentityDoc"
                            control={control}
                            rules={{
                                required: "The document type is required",
                            }}
                            render={({ field }) => (
                                <CustomSelect
                                    placeholder="Select document type"
                                    $width="100%"
                                    field={field}
                                    disableLabel
                                    options={identityOptions}
                                    onChange={field.onChange}
                                    lightBorder={true}
                                />
                            )}
                        />
                        <span className={"error"}>
                            {errors.accountType?.message}

                        </span>
                    </InputBox>
                )}

                {accountTypeWatch === "business" && (
                    <InputBox>
                        <Controller
                            name="identityNumber"
                            control={control}
                            rules={{
                                required: `Please enter the business registration number`,
                            }}
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Business registration number"
                                    value={field.value}
                                    labelColor="#FFFFFF"
                                    field={field}
                                    disableLabel
                                />
                            )}
                        />
                        <span className={"error"}>
                            {errors.identityNumber?.message}

                        </span>

                    </InputBox>
                )}

                <InputBox>
                    <Controller
                        name="recipientAddress"
                        control={control}
                        rules={{
                            required: "Please enter the Recipient Address",
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Recipient address"
                                labelColor="#FFFFFF"
                                field={field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.recipientAddress?.message}

                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="recipientEmail"
                        control={control}
                        rules={{
                            required: "Please enter the Recipient Email Address",
                            pattern: emailRegex,
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Recipient email address"
                                labelColor="#FFFFFF"
                                field={field}
                                disableLabel
                            />
                        )}
                    />

                    <span className={"error"}>
                        {errors.recipientEmail?.message}
                    </span>
                    {errors.recipientEmail?.type === "pattern" && (
                        <span className={"error"}>Invalid email format</span>
                    )}
                </InputBox>

                <InputBox>
                    <Controller
                        name="phoneNumber"
                        rules={{
                            required: "Phone number is required",
                        }}
                        control={control}
                        render={({ field }) => (
                            <InputPhone
                                label="Phone number"
                                field={field}
                                hasError={errors?.phoneNumber?.type === "required"}
                                defaultCountry="ZA"
                            />
                        )}
                    />
                    <span className={"error"}>{errors?.phoneNumber?.message}</span>
                </InputBox>

                {accountTypeWatch === "personal" && (
                    <InputBox>
                        <Controller
                            name="identityType"
                            control={control}
                            rules={{
                                required: "The field is required",
                            }}
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label={identityLabel}
                                    field={field}
                                    labelColor="#FFFFFF"
                                    disableLabel
                                />
                            )}
                        />
                        <span className={"error"}>
                            {errors.identityType?.message}

                        </span>
                    </InputBox>
                )}

                <InputBox>
                    <Controller
                        name="description"
                        control={control}
                        rules={{
                            required: false,
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Payment description (optional)"
                                field={field}
                                labelColor="#FFFFFF"
                                disableLabel
                            />
                        )}
                    />
                </InputBox>

                <ContinueButton>
                    <span className="form-label">
                        Funds should arrive within 1-3 working days.
                    </span>

                    <ItemWrapper>
                        <Button
                            onClick={handlePrevious}
                            label="Prev"
                            height="48px"
                            theme="alternate"
                            width="203px"
                        />

                        <Button
                            onClick={handleSubmit(resolveZARAccountNumber)}
                            label="Continue"
                            height="48px"
                            disabled={!isValid}
                        />
                    </ItemWrapper>
                </ContinueButton>
            </Form>
        </ContentWrapper>
    )
}

export default Beneficiary